/**
 * Имбалансы.
 *
 * Разрыв, который цена проскочила рывком: три свечи, у которых тени
 * крайних не перекрываются. По цене внутри почти не торговали, и рынок
 * обычно возвращается его закрыть — хотя бы до середины.
 *
 * На графике — полоса с пунктиром посередине и подписью «4ч имбаланс ▲»
 * у правого края. Можно показать разрывы и со старших таймфреймов: на
 * пятнадцатиминутном графике четырёхчасовой имбаланс виден как есть, а
 * его вес — толще кромка, плотнее заливка — говорит, что он важнее.
 *
 * Признак и порог размера — те же, что у условия «Разрыв цены (FVG)» в
 * конструкторе: `imbalanceZones` из общих правил.
 */

import { imbalanceZones, type ImbalanceZone } from '@specter/rules';
import { alignToTimeframe, timeframeMs, type Timeframe } from '@specter/types';
import type { Candle } from '../state/store.js';
import type { IndicatorShape, ReadoutLegendItem, ReadoutRow } from './overlays.js';
import {
  boolParam,
  colorParam,
  numberParam,
  type IndicatorContext,
  type IndicatorDefinition,
  type IndicatorParams,
} from './types.js';
import { TABLE_PARAM, percentWords, priceWords, situationRows, type Situation } from './explain.js';

const BULL = '#2EE6B0';
const BEAR = '#FF5470';

/** Старшие таймфреймы на выбор: ключ галочки и сам таймфрейм. */
export const FRAMES: ReadonlyArray<{ readonly key: string; readonly timeframe: Timeframe; readonly label: string }> = [
  { key: 'tf15m', timeframe: '15m', label: '15м' },
  { key: 'tf1H', timeframe: '1H', label: '1ч' },
  { key: 'tf4H', timeframe: '4H', label: '4ч' },
  { key: 'tf1D', timeframe: '1D', label: '1д' },
];

export const LABELS: Partial<Record<Timeframe, string>> = {
  '1m': '1м',
  '3m': '3м',
  '5m': '5м',
  '15m': '15м',
  '30m': '30м',
  '1H': '1ч',
  '2H': '2ч',
  '4H': '4ч',
  '6H': '6ч',
  '12H': '12ч',
  '1D': '1д',
  '3D': '3д',
  '1W': '1н',
  '1M': '1мес',
};

/** Зона с тем, откуда она: таймфрейм, подпись и вес. */
interface FramedZone extends ImbalanceZone {
  readonly frame: string;
  readonly weight: 1 | 2 | 3;
}

interface Found {
  readonly alive: readonly FramedZone[];
  readonly closed: readonly FramedZone[];
  /** Отмеченные старшие таймфреймы, чей ряд ещё не пришёл. */
  readonly loading: readonly string[];
}

/**
 * Ряд своего таймфрейма поглубже: тот, что пришёл с Core, плюс живой хвост графика.
 *
 * Последняя свеча загруженного ряда — снимок на момент загрузки: она
 * тогда ещё набиралась. Хвост графика заменяет её, а не встаёт следом,
 * иначе недостроенная свеча так и осталась бы в расчёте навсегда.
 */
export function deepOf(chart: readonly Candle[], context: IndicatorContext): readonly Candle[] {
  const loaded = context.series(context.timeframe);
  if (!loaded || loaded.length <= chart.length) return chart;
  const edge = loaded[loaded.length - 1]!.time;
  const tail = chart.filter((candle) => candle.time >= edge);
  if (tail.length === 0) return loaded;
  const from = tail[0]!.time;
  return [...loaded.filter((candle) => candle.time < from), ...tail];
}

let fresh = new Map<string, { loaded: readonly Candle[]; chart: readonly Candle[]; out: readonly Candle[] }>();

/**
 * Старший ряд, дотянутый до сейчас свечами графика.
 *
 * Старший ряд грузится с Core один раз — когда индикатор встал на
 * график — и дальше не обновляется. Через несколько часов он уже не
 * знал ни новых свечей, ни того, чем закончилась та, что тогда
 * набиралась: 4ч ордерблок на часовом графике «горел» после пробоя, а на
 * 4ч графике, где свечи живые, его уже не было.
 *
 * Свечи графика складываются в старшие по той же сетке, что у биржи:
 * у часовой свечи 13:00 место в четырёхчасовой 12:00. Пересобирается
 * всё начиная с последней загруженной — она тоже могла быть
 * недостроенной. Если график не покрывает её с начала, собирать не из
 * чего, и ряд остаётся как есть.
 */
export function freshOf(
  loaded: readonly Candle[] | null,
  chart: readonly Candle[],
  timeframe: Timeframe,
): readonly Candle[] | null {
  if (!loaded || loaded.length === 0 || chart.length === 0) return loaded;
  const cached = fresh.get(timeframe);
  if (cached && cached.loaded === loaded && cached.chart === chart) return cached.out;

  const from = loaded[loaded.length - 1]!.time;
  let out: readonly Candle[] = loaded;
  if (chart[0]!.time <= from) {
    const built: Candle[] = [];
    for (const candle of chart) {
      if (candle.time < from) continue;
      const bucket = alignToTimeframe(candle.time, timeframe);
      const top = built[built.length - 1];
      if (top && top.time === bucket) {
        built[built.length - 1] = {
          time: bucket,
          open: top.open,
          high: Math.max(top.high, candle.high),
          low: Math.min(top.low, candle.low),
          close: candle.close,
          volume: top.volume + candle.volume,
        };
      } else {
        built.push({ ...candle, time: bucket });
      }
    }
    if (built.length > 0) out = [...loaded.slice(0, -1), ...built];
  }

  // Держим по одному ряду на таймфрейм: старые снимки графика не нужны.
  if (fresh.size > 8) fresh = new Map();
  fresh.set(timeframe, { loaded, chart, out });
  return out;
}

/**
 * Где старшая зона закрылась на самом деле.
 *
 * Старший ряд знает только свечу, на которой зону прошли, — а это четыре
 * часа. Оборвать полосу на начале такой свечи значило бы нарисовать, что
 * зона кончилась раньше, чем до неё дошла цена. Точное место берётся по
 * свечам графика.
 */
function closedOnChart(zone: ImbalanceZone, chart: readonly Candle[]): number | null {
  if (zone.closedAt === null) return null;
  for (const candle of chart) {
    if (candle.time < zone.closedAt) continue;
    const through = zone.side === 'up' ? candle.low <= zone.lower : candle.high >= zone.upper;
    if (through) return candle.time;
  }
  return zone.closedAt;
}

let memo: { key: string; chart: readonly Candle[]; sources: readonly unknown[]; found: Found } | null = null;

function find(chart: readonly Candle[], p: IndicatorParams, context: IndicatorContext): Found {
  const minSize = numberParam(p, 'minSize', 0.25);
  const maxAlive = numberParam(p, 'maxAlive', 8);
  const maxClosed = boolParam(p, 'showClosed', true) ? numberParam(p, 'maxClosed', 4) : 0;
  const chartMs = timeframeMs(context.timeframe);

  /*
   * Что считать: свой таймфрейм, если отмечен, и отмеченные старшие.
   * Младшие и равный своему пропускаются — на часовом графике
   * пятнадцатиминутные разрывы превратили бы его в частокол, а часовой
   * «со стороны» — это тот же свой.
   */
  const wanted: Array<{ frame: string; weight: 1 | 2 | 3; bars: readonly Candle[] | null; live: boolean }> = [];
  if (boolParam(p, 'tfCurrent', true)) {
    wanted.push({ frame: LABELS[context.timeframe] ?? context.timeframe, weight: 1, bars: deepOf(chart, context), live: context.livePartial });
  }
  for (const [index, entry] of FRAMES.entries()) {
    if (!boolParam(p, entry.key, entry.timeframe === '4H')) continue;
    /*
     * Отмеченный таймфрейм, равный графику, — это свой: на дневном графике
     * галочка «1д» значит «покажи дневные». Раньше он пропускался вместе с
     * младшими, и при выключенном «своём таймфрейме» график оставался пустым.
     */
    if (entry.timeframe === context.timeframe) {
      if (!boolParam(p, 'tfCurrent', true)) wanted.push({ frame: entry.label, weight: 1, bars: deepOf(chart, context), live: context.livePartial });
      continue;
    }
    if (timeframeMs(entry.timeframe) <= chartMs) continue;
    // Старший ряд приходит с последней свечой, которая ещё набирается.
    wanted.push({
      frame: entry.label,
      weight: index >= 2 ? 3 : 2,
      bars: freshOf(context.series(entry.timeframe), chart, entry.timeframe),
      live: true,
    });
  }

  const key = `${minSize}|${maxAlive}|${maxClosed}|${context.timeframe}|${wanted.map((entry) => entry.frame).join(',')}`;
  const sources = wanted.map((entry) => entry.bars);
  if (
    memo &&
    memo.key === key &&
    memo.chart === chart &&
    memo.sources.length === sources.length &&
    memo.sources.every((source, index) => source === sources[index])
  ) {
    return memo.found;
  }

  const floor = chart[0]?.time ?? 0;
  const alive: FramedZone[] = [];
  const closed: FramedZone[] = [];
  const loading: string[] = [];

  for (const entry of wanted) {
    const bars = entry.bars;
    if (!bars || bars.length < 3) {
      if (entry.weight > 1) loading.push(entry.frame);
      continue;
    }
    // Разрыв, который рисует ещё не закрытая свеча, может исчезнуть к её
    // закрытию: такой не показывается, пока свеча не закрылась.
    const lastTime = bars[bars.length - 1]!.time;
    const zones = imbalanceZones(bars, minSize).filter((zone) => !(entry.live && zone.formedAt === lastTime));

    const own = zones
      .filter((zone) => zone.closedAt === null)
      .slice(-maxAlive)
      .map((zone) => ({ ...zone, frame: entry.frame, weight: entry.weight }));
    alive.push(...own);

    if (maxClosed > 0) {
      const done = zones
        .filter((zone) => zone.closedAt !== null && zone.closedAt >= floor)
        .slice(-maxClosed)
        .map((zone) => ({
          ...zone,
          closedAt: entry.weight > 1 ? closedOnChart(zone, chart) : zone.closedAt,
          frame: entry.frame,
          weight: entry.weight,
        }));
      closed.push(...done);
    }
  }

  const found = { alive, closed, loading };
  memo = { key, chart, sources, found };
  return found;
}

function labelOf(zone: FramedZone): string {
  if (zone.closedAt !== null) return `${zone.frame} · закрыт`;
  const arrow = zone.side === 'up' ? '▲' : '▼';
  const filled = Math.round(zone.filled * 100);
  return `${zone.frame} имбаланс ${arrow}${filled >= 5 ? ` · ${filled}%` : ''}`;
}

function shapesOf(chart: readonly Candle[], p: IndicatorParams, context: IndicatorContext): IndicatorShape[] {
  if (chart.length === 0) return [];
  const { alive, closed } = find(chart, p, context);
  const floor = chart[0]!.time;
  const bull = colorParam(p, 'bullColor', BULL);
  const bear = colorParam(p, 'bearColor', BEAR);
  const midline = boolParam(p, 'midline', true);
  const labels = boolParam(p, 'labels', true);
  const transparency = numberParam(p, 'opacity', 50);

  const shape = (zone: FramedZone): IndicatorShape => ({
    kind: 'zone',
    from: Math.max(zone.start, floor),
    to: zone.closedAt,
    upper: zone.upper,
    lower: zone.lower,
    tone: zone.side === 'up' ? 'up' : 'down',
    color: zone.side === 'up' ? bull : bear,
    midline,
    filled: zone.filled,
    weight: zone.weight,
    closed: zone.closedAt !== null,
    transparency,
    ...(labels ? { label: labelOf(zone) } : {}),
  });

  /*
   * Порядок — это и слои, и право на подпись: погасшие снизу, над ними
   * старшие, свои сверху. Подписи ставятся по тому же порядку, и место
   * у края первыми занимают старшие — их подпись важнее.
   */
  const byWeight = [...alive].sort((a, b) => b.weight - a.weight);
  return [...closed.map(shape), ...byWeight.map(shape)];
}

function sideWords(zone: FramedZone): string {
  return zone.side === 'up' ? 'бычий' : 'медвежий';
}

function spanWords(zone: FramedZone): string {
  return `${priceWords(zone.lower)} – ${priceWords(zone.upper)}`;
}

function readoutOf(chart: readonly Candle[], p: IndicatorParams, context: IndicatorContext): ReadoutRow[] {
  const last = chart.at(-1);
  if (!last) return [];
  const price = last.close;
  const { alive, loading } = find(chart, p, context);

  const inside = alive
    .filter((zone) => zone.lower <= price && price <= zone.upper)
    .sort((a, b) => b.weight - a.weight)[0];
  const above = alive.filter((zone) => zone.lower > price).sort((a, b) => a.lower - b.lower)[0];
  const below = alive.filter((zone) => zone.upper < price).sort((a, b) => b.upper - a.upper)[0];
  const upDistance = above ? ((above.lower - price) / price) * 100 : null;
  const downDistance = below ? ((price - below.upper) / price) * 100 : null;

  let situation: Situation;
  if (inside) {
    const filled = Math.round(inside.filled * 100);
    situation = {
      title: `Внутри ${inside.frame} имбаланса`,
      tone: inside.side === 'up' ? 'bull' : 'bear',
      text:
        inside.side === 'up'
          ? `Цена зашла в ${inside.frame} бычий имбаланс и заполнила его на ${filled}%. Такой разрыв часто держит цену как опора: пока его не прошли насквозь, покупатели его защищают.`
          : `Цена зашла в ${inside.frame} медвежий имбаланс и заполнила его на ${filled}%. Такой разрыв часто давит сверху: пока его не прошли насквозь, продавцы его защищают.`,
    };
  } else if (upDistance === null && downDistance === null) {
    situation = {
      title: 'Рядом пусто',
      tone: 'muted',
      text: 'Незакрытых имбалансов нет — рынок закрыл все разрывы, которые оставлял. Цене нечего «дозаполнять».',
    };
  } else if (downDistance === null || (upDistance !== null && upDistance < downDistance)) {
    situation = {
      title: 'Ближе сверху',
      tone: 'bull',
      text: `Ближайший незакрытый разрыв — ${above!.frame} ${sideWords(above!)}, сверху, в ${percentWords(upDistance!).replace('+', '')} от цены. Рынок часто возвращается закрыть такие разрывы — хотя бы до середины.`,
    };
  } else {
    situation = {
      title: 'Ближе снизу',
      tone: 'bear',
      text: `Ближайший незакрытый разрыв — ${below!.frame} ${sideWords(below!)}, снизу, в ${percentWords(downDistance).replace('+', '')} от цены. Рынок часто возвращается закрыть такие разрывы — хотя бы до середины.`,
    };
  }

  const rows: ReadoutRow[] = [...situationRows(situation)];
  if (above && upDistance !== null) {
    rows.push({ label: 'Сверху', value: `${above.frame} · ${spanWords(above)} · ${percentWords(upDistance, 2)}`, tone: 'down' });
  }
  if (below && downDistance !== null) {
    rows.push({ label: 'Снизу', value: `${below.frame} · ${spanWords(below)} · ${percentWords(-downDistance, 2)}`, tone: 'up' });
  }
  const bulls = alive.filter((zone) => zone.side === 'up').length;
  rows.push({ label: 'Незакрытых', value: `${bulls} бычьих · ${alive.length - bulls} медвежьих`, tone: 'muted' });
  if (loading.length > 0) rows.push({ label: 'Старшие', value: `${loading.join(', ')} грузятся…`, tone: 'muted' });
  return rows;
}

const LEGEND: readonly ReadoutLegendItem[] = [
  {
    sample: 'solid',
    term: 'Полоса',
    text: 'разрыв, который цена проскочила рывком. Зелёная — под ценой, после рывка вверх; красная — над ценой, после рывка вниз.',
  },
  { sample: 'dashed', term: 'Пунктир', text: 'середина разрыва — к ней цена возвращается чаще всего.' },
  { sample: 'soft', term: 'Бледная часть', text: 'то, что цена уже заполнила. Сколько — написано в подписи процентом.' },
  { sample: 'strong', term: 'Толстая кромка', text: 'разрыв со старшего таймфрейма: 4ч и 1д весомее своего.' },
  { sample: 'faded', term: 'Погасшая', text: 'закрытый разрыв — цена прошла его насквозь.' },
];

export const IMBALANCES: IndicatorDefinition = {
  id: 'imbalances',
  name: 'Имбалансы',
  description:
    'Разрывы, которые цена проскочила рывком, — со своего таймфрейма и со старших. ' +
    'Пунктир — середина, подпись — откуда зона и насколько заполнена.',
  author: 'specter',
  placement: 'overlay',
  params: [
    {
      key: 'tfCurrent',
      label: 'Имбалансы своего таймфрейма',
      hint: 'Разрывы на свечах открытого графика. Выключите, если нужны только старшие.',
      type: 'boolean',
      default: true,
    },
    {
      key: 'tf15m',
      label: 'Имбалансы 15м',
      hint: 'Показывать разрывы пятнадцатиминутного графика. Видны только на графиках младше 15м.',
      type: 'boolean',
      default: false,
    },
    {
      key: 'tf1H',
      label: 'Имбалансы 1ч',
      hint: 'Показывать разрывы часового графика. Видны на графиках младше часа.',
      type: 'boolean',
      default: false,
    },
    {
      key: 'tf4H',
      label: 'Имбалансы 4ч',
      hint: 'Показывать разрывы четырёхчасового графика. Видны на графиках младше 4ч — толще кромка, весомее зона.',
      type: 'boolean',
      default: true,
    },
    {
      key: 'tf1D',
      label: 'Имбалансы 1д',
      hint: 'Показывать разрывы дневного графика. Самые весомые — к ним цена возвращается неделями.',
      type: 'boolean',
      default: false,
    },
    {
      key: 'minSize',
      label: 'Наименьший разрыв, доля обычной свечи',
      hint: 'Разрыв меньше этой доли от обычного размаха свечи не показывается. Выше — только заметные разрывы; ниже — каждая щель.',
      type: 'number',
      default: 0.25,
      min: 0,
      max: 3,
      step: 0.05,
    },
    {
      key: 'maxAlive',
      label: 'Незакрытых на таймфрейм',
      hint: 'Сколько последних незакрытых разрывов держать с каждого таймфрейма. Больше — полнее картина, но теснее на графике.',
      type: 'number',
      default: 8,
      min: 1,
      max: 30,
      step: 1,
    },
    {
      key: 'showClosed',
      label: 'Показывать закрытые',
      hint: 'Разрывы, которые цена уже прошла насквозь: гаснут и обрываются там, где их закрыли. Полезно видеть, как рынок их отрабатывал.',
      type: 'boolean',
      default: true,
    },
    {
      key: 'maxClosed',
      label: 'Закрытых на таймфрейм',
      hint: 'Сколько последних закрытых разрывов показывать с каждого таймфрейма.',
      type: 'number',
      default: 4,
      min: 1,
      max: 30,
      step: 1,
    },
    {
      key: 'midline',
      label: 'Пунктир по середине',
      hint: 'Середина разрыва: к ней цена возвращается чаще всего, и там часто разворачивается.',
      type: 'boolean',
      default: true,
    },
    {
      key: 'labels',
      label: 'Подписи',
      hint: 'Пилюля у правого края зоны: таймфрейм, сторона и насколько разрыв уже заполнен.',
      type: 'boolean',
      default: true,
    },
    {
      key: 'notifyEnter',
      label: 'Оповещать: цена вошла в зону',
      hint: 'Когда включены уведомления — сообщать, как только свеча впервые зашла в незакрытый имбаланс.',
      type: 'boolean',
      default: true,
    },
    {
      key: 'notifyMid',
      label: 'Оповещать: цена дошла до середины',
      hint: 'Когда включены уведомления — сообщать, когда цена заполнила имбаланс наполовину: там она чаще всего и разворачивается.',
      type: 'boolean',
      default: true,
    },
    {
      key: 'bullColor',
      label: 'Цвет бычьих',
      hint: 'Каким цветом рисовать разрывы под ценой — те, что остались после рывка вверх и держат цену снизу.',
      type: 'color',
      default: BULL,
    },
    {
      key: 'bearColor',
      label: 'Цвет медвежьих',
      hint: 'Каким цветом рисовать разрывы над ценой — те, что остались после рывка вниз и давят сверху.',
      type: 'color',
      default: BEAR,
    },
    {
      key: 'opacity',
      label: 'Прозрачность заливки, %',
      hint: 'Больше процент — бледнее заливка, и свечи под ней видно лучше.',
      type: 'number',
      default: 50,
      min: 0,
      max: 95,
      step: 5,
    },
    TABLE_PARAM,
  ],
  outputs: [],
  timeframesOf: (p, chart) => [
    ...(boolParam(p, 'tfCurrent', true) ? [chart] : []),
    ...FRAMES.filter((entry) => boolParam(p, entry.key, entry.timeframe === '4H') && timeframeMs(entry.timeframe) > timeframeMs(chart)).map(
      (entry) => entry.timeframe,
    ),
  ],
  warmup: () => 0,
  calc: () => ({}),
  shapes: shapesOf,
  readout: readoutOf,
  legend: LEGEND,
  label: () => 'Имбалансы',
};
